import React from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Code2,
  ExternalLink,
  FileText,
  FolderOpen,
  Layers3,
  Loader2,
  Share2,
  Sparkles,
  Trash2,
  UserPlus,
  UserMinus,
  Users,
  X,
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { HistoryDashboardShell } from '../shared/HistoryDashboardShell';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const getTotalCasesFromPreview = (preview) => {
  if (!preview || typeof preview !== 'object') return 0;
  return Number(preview.total_count || 0);
};

const formatApprovalLabel = (approvalState) => {
  const value = String(approvalState || '').toLowerCase();
  if (value === 'pending_approval') return 'Pending Approval';
  if (value === 'approved') return 'Approved';
  if (value === 'rejected') return 'Rejected';
  return 'Uploaded';
};

const approvalBadgeClass = (approvalState) => {
  const value = String(approvalState || '').toLowerCase();
  if (value === 'pending_approval') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (value === 'approved') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (value === 'rejected') return 'border-rose-200 bg-rose-50 text-rose-700';
  return 'border-amber-200 bg-amber-50 text-amber-700';
};

const HistoryDashboardDetailsSkeleton = () => (
  <div className="space-y-4" role="status" aria-label="Loading BRD details">
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {[0, 1, 2].map((card) => (
        <div
          key={`history-summary-loading-${card}`}
          className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm"
        >
          <div className="animate-pulse space-y-3">
            <div className="h-3 w-4/5 rounded bg-slate-100" />
            <div className="flex items-center justify-between">
              <div className="h-7 w-10 rounded bg-slate-200" />
              <div className="h-4 w-4 rounded bg-slate-100" />
            </div>
          </div>
        </div>
      ))}
    </div>

    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/80 px-4 py-3">
        <div className="h-4 w-44 animate-pulse rounded bg-slate-200" />
        <div className="h-7 w-24 animate-pulse rounded-full bg-slate-100" />
      </div>
      <div className="divide-y divide-slate-100">
        {[0, 1, 2].map((row) => (
          <div key={`history-section-loading-${row}`} className="px-4 py-4">
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-3/4 rounded bg-slate-200" />
              <div className="h-3 w-1/2 rounded bg-slate-100" />
              <div className="grid gap-3 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_80px]">
                <div />
                <div className="h-8 rounded-md bg-blue-50" />
                <div className="h-8 rounded-md bg-indigo-50" />
                <div className="h-8 rounded-md bg-slate-100" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  </div>
);

const HistoryDashboardSkeleton = () => (
  <div className="space-y-4" role="status" aria-label="Loading BRD history">
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="animate-pulse space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="h-3 w-28 rounded bg-blue-100" />
            <div className="h-6 w-3/5 rounded bg-slate-200" />
            <div className="h-3 w-2/5 rounded bg-slate-100" />
            <div className="flex flex-wrap gap-2">
              <div className="h-8 w-36 rounded-md bg-slate-100" />
              <div className="h-8 w-24 rounded-md bg-slate-100" />
              <div className="h-8 w-20 rounded-md bg-slate-100" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="h-10 w-36 rounded-xl bg-blue-100" />
            <div className="h-10 w-28 rounded-xl bg-emerald-50" />
            <div className="h-10 w-28 rounded-xl bg-rose-50" />
          </div>
        </div>
      </div>
    </div>

    <HistoryDashboardDetailsSkeleton />
  </div>
);

export const PreviousPDFsModal = ({
  historyMode = 'brd',
  previousPDFs = [],
  previousPDFsLoading = false,
  previousPDFsError = null,
  historyRestoreLoading = null,
  dashboardPdf = null,
  dashboardData = null,
  dashboardLoading = false,
  dashboardError = null,
  sharingStatus = null,
  sharingRequests = [],
  sharingLoading = false,
  canRequestSharing = false,
  canApproveSharing = false,
  onPreview,
  onSelect,
  onDeletePdf,
  onOpenSection,
  onRequestShare,
  onApproveShareRequest,
  onRejectShareRequest,
  onRevokeShareAccess,
  onClose,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteTargetPdf, setDeleteTargetPdf] = React.useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = React.useState('');
  const [deleteError, setDeleteError] = React.useState('');
  const [deleteSaving, setDeleteSaving] = React.useState(false);
  const [sharingPanelOpen, setSharingPanelOpen] = React.useState(false);
  const [selectedShareUserId, setSelectedShareUserId] = React.useState('');
  const [sharingActionError, setSharingActionError] = React.useState('');
  const [revokeTargetUser, setRevokeTargetUser] = React.useState(null);
  const [revokeError, setRevokeError] = React.useState('');
  const [revokeSaving, setRevokeSaving] = React.useState(false);
  const isHistoryRestoring = Boolean(historyRestoreLoading);
  const normalizedHistoryMode = String(historyMode || '').trim().toLowerCase();
  const isRequirementsHistory = normalizedHistoryMode === 'requirements';
  const isOpenApiHistory = ['openapi', 'api', 'swagger'].includes(normalizedHistoryMode);
  const isExcelHistory = ['testcase_excel', 'test_case_excel', 'excel', 'excel_test_cases'].includes(normalizedHistoryMode);
  const historyLabels = isOpenApiHistory
    ? {
        dashboardKicker: 'API Contract Dashboard',
        dashboardTitle: 'Previous API Contracts, Test Cases, and Scripts',
        dashboardCopy: 'Reuse saved Swagger/OpenAPI contracts and jump straight to generated API outputs.',
        listTitle: 'API Contracts',
        selectedTitle: 'Selected API Contract',
        generatedTitle: 'Generated Operations',
        testCaseTitle: 'Operations with Test Cases',
        scriptsTitle: 'Operations with Scripts',
        loadLabel: 'Load This Contract',
        loadingLabel: 'Loading Contract...',
        emptyList: 'No saved API contracts found.',
        loadErrorTitle: 'Could not load API contract history.',
        selectPrompt: 'Select an API contract from the left to view its dashboard.',
        selectCopy: 'You can preview generated API outputs and load the contract without leaving this application flow.',
        deleteHistoryTitle: 'Delete API Contract History',
        deleteButton: 'Delete Contract',
        deleteTitle: (name) => `Delete ${name || 'this API contract'}?`,
        deleteCopy: 'This will remove this API contract and all saved generated history.',
        sectionCoverageTitle: 'Operation Output Coverage',
        noGeneratedSections: 'No generated operations yet for this API contract.',
        itemCountLabel: 'Operations',
        rowCountLabel: 'operations',
        mappingTitle: 'Contract mapping',
        mappingVersionPrefix: 'Contract',
      }
    : isExcelHistory
    ? {
        dashboardKicker: 'Excel Testcase Dashboard',
        dashboardTitle: 'Previous Excel Test Cases and Scripts',
        dashboardCopy: 'Reuse saved workbook imports and jump straight to reviewed cases or generated scripts.',
        listTitle: 'Excel Testcase Sources',
        selectedTitle: 'Selected Workbook',
        generatedTitle: 'Imported Case Groups',
        testCaseTitle: 'Groups with Test Cases',
        scriptsTitle: 'Groups with Scripts',
        loadLabel: 'Load This Workbook',
        loadingLabel: 'Loading Workbook...',
        emptyList: 'No saved Excel testcase imports found.',
        loadErrorTitle: 'Could not load Excel testcase history.',
        selectPrompt: 'Select a workbook import from the left to view its dashboard.',
        selectCopy: 'You can preview imported outputs and load the source without leaving this application flow.',
        deleteHistoryTitle: 'Delete Excel Testcase History',
        deleteButton: 'Delete Workbook',
        deleteTitle: (name) => `Delete ${name || 'this workbook import'}?`,
        deleteCopy: 'This will remove this workbook import and all saved generated history.',
        sectionCoverageTitle: 'Imported Group Output Coverage',
        noGeneratedSections: 'No imported groups yet for this workbook.',
        itemCountLabel: 'Case Groups',
        rowCountLabel: 'groups',
        mappingTitle: 'Workbook mapping',
        mappingVersionPrefix: 'Workbook',
      }
    : isRequirementsHistory
    ? {
        dashboardKicker: 'Requirements Dashboard',
        dashboardTitle: 'Previous Requirements, Test Cases, and Scripts',
        dashboardCopy: 'Reuse saved requirement sources and jump straight to the latest outputs.',
        listTitle: 'Requirement Sources',
        selectedTitle: 'Selected Requirement',
        generatedTitle: 'Generated Requirements',
        testCaseTitle: 'Requirements with Test Cases',
        scriptsTitle: 'Requirements with Scripts',
        loadLabel: 'Load This Requirement',
        loadingLabel: 'Loading Requirement...',
        emptyList: 'No saved requirement sources found.',
        loadErrorTitle: 'Could not load requirement history.',
        selectPrompt: 'Select a requirement source from the left to view its dashboard.',
        selectCopy: 'You can preview generated outputs and load the source without leaving this application flow.',
        deleteHistoryTitle: 'Delete Requirement History',
        deleteButton: 'Delete Source',
        deleteTitle: (name) => `Delete ${name || 'this requirement source'}?`,
        deleteCopy: 'This will remove this requirement source and all saved generated history.',
        sectionCoverageTitle: 'Requirement Output Coverage',
        noGeneratedSections: 'No generated outputs yet for this requirement source.',
        itemCountLabel: 'Requirements',
        rowCountLabel: 'requirements',
        mappingTitle: 'Source mapping',
        mappingVersionPrefix: 'Source',
      }
    : {
        dashboardKicker: 'Knowledge Dashboard',
        dashboardTitle: 'Previous BRDs, Test Cases, and Scripts',
        dashboardCopy: 'Reuse processed documents and jump straight to the latest section outputs.',
        listTitle: 'Processed BRDs',
        selectedTitle: 'Selected BRD',
        generatedTitle: 'Generated Sections',
        testCaseTitle: 'Sections with Test Cases',
        scriptsTitle: 'Sections with Scripts',
        loadLabel: 'Load This BRD',
        loadingLabel: 'Loading BRD...',
        emptyList: 'No previously processed BRDs found.',
        loadErrorTitle: 'Could not load BRD history.',
        selectPrompt: 'Select a BRD from the left to view its dashboard.',
        selectCopy: 'You can preview generated outputs and load the file without leaving this application flow.',
        deleteHistoryTitle: 'Delete BRD History',
        deleteButton: 'Delete BRD',
        deleteTitle: (name) => `Delete ${name || 'this BRD'}?`,
        deleteCopy: 'This will remove this BRD and all saved generated history.',
        sectionCoverageTitle: 'Section Output Coverage',
        noGeneratedSections: 'No generated sections yet for this BRD.',
        itemCountLabel: 'Sections',
        rowCountLabel: 'sections',
        mappingTitle: 'BRD mapping',
        mappingVersionPrefix: 'BRD',
      };

  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  React.useEffect(() => {
    setSharingPanelOpen(false);
    setSelectedShareUserId('');
    setSharingActionError('');
    setRevokeTargetUser(null);
    setRevokeError('');
    setRevokeSaving(false);
  }, [dashboardPdf?.pdf_id]);

  const openDeleteDialog = (pdf) => {
    if (!pdf?.pdf_id) return;
    setDeleteTargetPdf(pdf);
    setDeleteConfirmText('');
    setDeleteError('');
    setDeleteSaving(false);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteSaving) return;
    setDeleteDialogOpen(false);
    setDeleteTargetPdf(null);
    setDeleteConfirmText('');
    setDeleteError('');
    setDeleteSaving(false);
  };

  const confirmDeletePdf = async () => {
    if (!deleteTargetPdf?.pdf_id || !onDeletePdf) return;
    if (deleteConfirmText.trim().toLowerCase() !== 'delete this') {
      setDeleteError('Type "delete this" to confirm deletion.');
      return;
    }

    setDeleteSaving(true);
    setDeleteError('');
    try {
      await onDeletePdf(deleteTargetPdf);
      setDeleteDialogOpen(false);
      setDeleteTargetPdf(null);
      setDeleteConfirmText('');
      setDeleteError('');
      setDeleteSaving(false);
    } catch (error) {
      setDeleteError(
        error.message
        || `Failed to delete ${isRequirementsHistory ? 'requirement source' : isExcelHistory ? 'workbook import' : 'BRD'}.`
      );
      setDeleteSaving(false);
    }
  };

  const rawSections = Array.isArray(dashboardData?.sections) ? dashboardData.sections : [];
  const sections = rawSections.filter((section) => {
    const savedTestCases = Number(section?.test_cases?.count || 0);
    const savedScripts = Number(section?.scripts?.count || 0);
    return savedTestCases > 0 || savedScripts > 0;
  });
  const summary = dashboardData?.summary || {};
  const brdVersions = Array.isArray(dashboardData?.brd_versions) ? dashboardData.brd_versions : [];
  const activeBrdVersion = dashboardData?.active_brd_version ?? null;
  const openBrdVersionSource = (version) => {
    const pdfId = dashboardPdf?.pdf_id;
    if (!pdfId || !version) return;
    window.open(
      `${API_BASE_URL}${API_ENDPOINTS.BRD_VERSION_SOURCE(pdfId, version)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };
  const activePdfId = String(dashboardPdf?.pdf_id || '').trim();
  const normalizedSharingStatus =
    sharingStatus &&
    typeof sharingStatus === 'object' &&
    String(sharingStatus?.pdf_id || '').trim() === activePdfId
      ? sharingStatus
      : null;
  const sharedUsers = Array.isArray(normalizedSharingStatus?.shared_users)
    ? normalizedSharingStatus.shared_users
    : [];
  const eligibleShareUsers = Array.isArray(normalizedSharingStatus?.eligible_users)
    ? normalizedSharingStatus.eligible_users
    : [];
  const pendingShareRequests = (Array.isArray(sharingRequests) ? sharingRequests : [])
    .filter((requestRow) => {
      const rowPdfId = String(requestRow?.pdf_id || activePdfId || '').trim();
      return rowPdfId === activePdfId && String(requestRow?.status || '').toLowerCase() === 'pending';
    });
  const pendingShareTargetIds = new Set(
    pendingShareRequests.map((requestRow) => String(requestRow?.target_user_id || '').trim()).filter(Boolean)
  );
  const canDirectShare = Boolean(normalizedSharingStatus?.can_direct_share) || canApproveSharing;
  const canRequestPdfShare = Boolean(normalizedSharingStatus?.can_request_share) || canRequestSharing;
  const canShareWithUser = canDirectShare || canRequestPdfShare;
  const selectedShareIsPending = pendingShareTargetIds.has(String(selectedShareUserId || '').trim());
  const canShowSharingButton = !isRequirementsHistory && !isExcelHistory && Boolean(dashboardPdf?.application_id) && (
    canShareWithUser ||
    canApproveSharing ||
    Boolean(normalizedSharingStatus)
  );
  const shareActionLabel = canDirectShare
    ? (selectedShareIsPending ? 'Approve & Share' : 'Share BRD')
    : (selectedShareIsPending ? 'Pending' : 'Request Share');
  const shareActionDisabled =
    sharingLoading ||
    !selectedShareUserId ||
    (selectedShareIsPending && !canDirectShare);

  const openSharingDialog = () => {
    setSharingPanelOpen(true);
    setSharingActionError('');
  };

  const closeSharingDialog = () => {
    if (sharingLoading) return;
    setSharingPanelOpen(false);
    setSelectedShareUserId('');
    setSharingActionError('');
  };

  const handleShareRequest = async () => {
    const targetUserId = String(selectedShareUserId || '').trim();
    if (!activePdfId || !targetUserId || typeof onRequestShare !== 'function') return;
    setSharingActionError('');
    try {
      await onRequestShare({ pdfId: activePdfId, targetUserId });
      setSelectedShareUserId('');
    } catch (error) {
      setSharingActionError(error?.message || 'Failed to request BRD sharing.');
    }
  };

  const handleApproveShareRequest = async (requestId) => {
    if (!activePdfId || !requestId || typeof onApproveShareRequest !== 'function') return;
    setSharingActionError('');
    try {
      await onApproveShareRequest(requestId, activePdfId);
    } catch (error) {
      setSharingActionError(error?.message || 'Failed to approve BRD sharing.');
    }
  };

  const handleRejectShareRequest = async (requestId) => {
    if (!activePdfId || !requestId || typeof onRejectShareRequest !== 'function') return;
    setSharingActionError('');
    try {
      await onRejectShareRequest(requestId, activePdfId);
    } catch (error) {
      setSharingActionError(error?.message || 'Failed to reject BRD sharing.');
    }
  };

  const openRevokeDialog = (user) => {
    if (!user?.id) return;
    setRevokeTargetUser(user);
    setRevokeError('');
  };

  const closeRevokeDialog = () => {
    if (revokeSaving) return;
    setRevokeTargetUser(null);
    setRevokeError('');
  };

  const confirmRevokeShareAccess = async () => {
    const user = revokeTargetUser;
    const targetUserId = String(user?.id || '').trim();
    if (!activePdfId || !targetUserId || typeof onRevokeShareAccess !== 'function') return;
    setSharingActionError('');
    setRevokeError('');
    setRevokeSaving(true);
    try {
      await onRevokeShareAccess(targetUserId, activePdfId);
      setRevokeTargetUser(null);
    } catch (error) {
      setRevokeError(error?.message || 'Failed to remove BRD access.');
    } finally {
      setRevokeSaving(false);
    }
  };

  const modalContent = (
    <>
      <HistoryDashboardShell
        ariaLabel={historyLabels.dashboardKicker}
        kicker={historyLabels.dashboardKicker}
        title={historyLabels.dashboardTitle}
        copy={historyLabels.dashboardCopy}
        onClose={onClose}
        closeAriaLabel="Close dashboard"
        sidebar={(
          <>
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                {historyLabels.listTitle}
              </p>
              <span className="inline-flex min-w-[28px] items-center justify-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                {previousPDFsLoading ? (
                  <span className="h-3 w-4 animate-pulse rounded bg-slate-200" />
                ) : (
                  previousPDFs.length
                )}
              </span>
            </div>

            <div className="space-y-3">
              {previousPDFsLoading ? (
                [0, 1, 2].map((row) => (
                  <div
                    key={`history-loading-${row}`}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm"
                  >
                    <div className="animate-pulse space-y-3">
                      <div className="h-4 w-4/5 rounded bg-slate-200" />
                      <div className="h-3 w-3/5 rounded bg-slate-100" />
                      <div className="flex gap-2">
                        <div className="h-7 w-20 rounded-lg bg-slate-100" />
                        <div className="h-7 w-16 rounded-lg bg-slate-100" />
                      </div>
                      <div className="h-3 w-1/2 rounded bg-slate-100" />
                    </div>
                  </div>
                ))
              ) : previousPDFsError ? (
                <div className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>{previousPDFsError}</span>
                </div>
              ) : previousPDFs.length > 0 ? (
                previousPDFs.map((pdf) => {
                  const isActive = dashboardPdf?.pdf_id === pdf.pdf_id;
                  return (
                    <button
                      key={pdf.pdf_id}
                      type="button"
                      onClick={() => {
                        if (!isHistoryRestoring) {
                          onPreview(pdf);
                        }
                      }}
                      disabled={isHistoryRestoring}
                      className={`group w-full rounded-2xl border px-4 py-3.5 text-left transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                        isActive
                          ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-cyan-50/60 shadow-[0_10px_28px_rgba(37,99,235,0.16)]'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">
                            {pdf.pdf_file}
                          </p>
                          <p className="mt-1 truncate text-xs text-slate-500">{pdf.pdf_id}</p>
                        </div>
                        {isActive && <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-blue-600" />}
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
                        <span className="rounded-lg border border-slate-200 bg-white px-2 py-1">
                          {historyLabels.itemCountLabel} {pdf.sections_count ?? 0}
                        </span>
                        <span className="rounded-lg border border-slate-200 bg-white px-2 py-1">
                          Images {pdf.total_images ?? 0}
                        </span>
                        {pdf.approval_required && (
                          <span className={`rounded-lg border px-2 py-1 ${approvalBadgeClass(pdf.approval_state)}`}>
                            {formatApprovalLabel(pdf.approval_state)}
                          </span>
                        )}
                      </div>

                      <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {formatDate(pdf.processed_at)}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                  {historyLabels.emptyList}
                </div>
              )}
            </div>
          </>
        )}
      >

            {previousPDFsLoading ? (
              <HistoryDashboardSkeleton />
            ) : previousPDFsError ? (
              <div className="flex min-h-[280px] items-center justify-center rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center">
                <div>
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-rose-600">
                    <AlertCircle className="h-6 w-6" />
                  </div>
                  <p className="mt-4 text-sm font-semibold text-rose-800">
                    {historyLabels.loadErrorTitle}
                  </p>
                  <p className="mt-1 text-xs text-rose-700">
                    {previousPDFsError}
                  </p>
                </div>
              </div>
            ) : dashboardPdf ? (
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm">
                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">
                        {historyLabels.selectedTitle}
                      </p>
                      <h3 className="mt-1 truncate text-xl font-bold text-slate-900">
                        {dashboardPdf.pdf_file}
                      </h3>
                      <p className="mt-1 truncate text-xs text-slate-500">{dashboardPdf.pdf_id}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                        <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1">
                          <CalendarDays className="h-3.5 w-3.5" />
                          {formatDate(dashboardPdf.processed_at)}
                        </span>
                        <span className="rounded-md border border-slate-200 bg-white px-2.5 py-1">
                          {historyLabels.itemCountLabel} {dashboardPdf.sections_count ?? 0}
                        </span>
                        <span className="rounded-md border border-slate-200 bg-white px-2.5 py-1">
                          Images {dashboardPdf.total_images ?? 0}
                        </span>
                        {dashboardPdf.approval_required && (
                          <span
                            className={`rounded-md border px-2.5 py-1 ${approvalBadgeClass(
                              dashboardPdf.approval_state
                            )}`}
                          >
                            {formatApprovalLabel(dashboardPdf.approval_state)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          if (!isHistoryRestoring) {
                            onSelect(dashboardPdf);
                          }
                        }}
                        disabled={isHistoryRestoring}
                        className="primary-btn px-4 py-2.5 text-sm"
                      >
                        <FolderOpen className="h-4 w-4" />
                        {isHistoryRestoring ? historyLabels.loadingLabel : historyLabels.loadLabel}
                      </button>
                      {canShowSharingButton && (
                        <button
                          type="button"
                          onClick={openSharingDialog}
                          disabled={isHistoryRestoring}
                          className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
                        >
                          {sharingLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Share2 className="h-4 w-4" />
                          )}
                          Share BRD
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openDeleteDialog(dashboardPdf)}
                        disabled={isHistoryRestoring}
                        className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Trash2 className="h-4 w-4" />
                        {historyLabels.deleteButton}
                      </button>
                    </div>
                  </div>
                </div>

                {(dashboardLoading || isHistoryRestoring) && (
                  <HistoryDashboardDetailsSkeleton />
                )}

                {!dashboardLoading && !isHistoryRestoring && dashboardError && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                    {dashboardError}
                  </div>
                )}

                {!dashboardLoading && !isHistoryRestoring && !dashboardError && (
                  <div className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      <div className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                          {historyLabels.generatedTitle}
                        </p>
                        <div className="mt-2 flex items-center justify-between">
                          <p className="text-2xl font-bold text-slate-900">{sections.length}</p>
                          <Layers3 className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>

                      <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-3.5 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-700">
                          {historyLabels.testCaseTitle}
                        </p>
                        <div className="mt-2 flex items-center justify-between">
                          <p className="text-2xl font-bold text-blue-700">
                            {summary.sections_with_test_cases ?? 0}
                          </p>
                          <FileText className="h-4 w-4 text-blue-500" />
                        </div>
                      </div>

                      <div className="rounded-2xl border border-indigo-100 bg-indigo-50/75 p-3.5 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-700">
                          {historyLabels.scriptsTitle}
                        </p>
                        <div className="mt-2 flex items-center justify-between">
                          <p className="text-2xl font-bold text-indigo-700">
                            {summary.sections_with_scripts ?? 0}
                          </p>
                          <Code2 className="h-4 w-4 text-indigo-500" />
                        </div>
                      </div>

                    </div>

                    {!isRequirementsHistory && !isOpenApiHistory && !isExcelHistory && brdVersions.length > 0 && (
                      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/80 px-4 py-3">
                          <p className="text-sm font-semibold text-slate-800">BRD Version History</p>
                          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                            {brdVersions.length} version{brdVersions.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {brdVersions.map((version) => (
                            <div key={version.id || version.version} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                              <div>
                                <p className="text-sm font-semibold text-slate-900">
                                  v{version.version}
                                  {Number(version.version) === Number(activeBrdVersion) ? ' · Active' : ''}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {version.filename || 'BRD source'} · {formatDate(version.updated_at || version.created_at)}
                                </p>
                                {version.change_summary && (
                                  <p className="mt-1 text-xs text-slate-600">
                                    {Number(version.change_summary.total_changes || 0)} change{Number(version.change_summary.total_changes || 0) === 1 ? '' : 's'}
                                  </p>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => openBrdVersionSource(version.version)}
                                className="inline-flex items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                                View BRD
                              </button>
                            </div>
                          ))}
                        </div>
                      </section>
                    )}

                    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/80 px-4 py-3">
                        <p className="text-sm font-semibold text-slate-800">{historyLabels.sectionCoverageTitle}</p>
                        <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                          {sections.length} {historyLabels.rowCountLabel}
                        </span>
                      </div>

                      <div className="max-h-[50vh] overflow-auto">
                        {sections.length > 0 ? (
                          <div className="divide-y divide-slate-100">
                            {sections.map((section) => {
                              const tc = section.test_cases || {};
                              const script = section.scripts || {};
                              const tcPreview = tc.preview || {};
                              const scriptPreview = script.preview || {};
                              const testCaseVersions = Array.isArray(tc.version_details)
                                ? tc.version_details
                                : [];
                              const testCaseVersionsByBrd = new Map();
                              testCaseVersions.forEach((version) => {
                                const brdVersion = Number(version?.brd_version || 0);
                                const groupKey = brdVersion > 0 ? `brd-${brdVersion}` : `tc-${version?.version}`;
                                const existing = testCaseVersionsByBrd.get(groupKey);
                                const versionNumber = Number(version?.version || 0);
                                const existingVersionNumber = Number(existing?.version || 0);
                                if (
                                  !existing
                                  || version?.active
                                  || (!existing.active && versionNumber >= existingVersionNumber)
                                ) {
                                  testCaseVersionsByBrd.set(groupKey, version);
                                }
                              });
                              const displayTestCaseVersions = Array.from(testCaseVersionsByBrd.values());
                              const activeTestCaseVersion =
                                tc.active_version
                                || displayTestCaseVersions.find((item) => item?.active)?.version
                                || displayTestCaseVersions[0]?.version
                                || null;
                              const openSection = (artifactVersion = null) =>
                                !isHistoryRestoring && onOpenSection?.({
                                  pdf: dashboardPdf,
                                  sectionNumber: section.section_number,
                                  targetStep: 5,
                                  artifactVersion,
                                });

                              return (
                                <div
                                  key={section.section_number}
                                  className={`group px-4 py-4 transition-colors ${
                                    isHistoryRestoring
                                      ? 'cursor-not-allowed opacity-60'
                                      : 'cursor-pointer hover:bg-blue-50/45'
                                  }`}
                                  onClick={() => openSection(activeTestCaseVersion)}
                                >
                                  <div className="flex flex-col gap-3 xl:grid xl:grid-cols-[minmax(0,2.2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_auto] xl:items-center">
                                    <div>
                                      <p className="text-sm font-semibold leading-6 text-slate-900">
                                        {section.section_number}. {section.section_title}
                                      </p>
                                      <p className="mt-1 text-xs text-slate-500">
                                        Pages {section.indexed_pages || '-'} · Images {section.image_count ?? 0} · Updated {formatDate(tc.latest_updated_at || script.latest_updated_at)}
                                      </p>
                                    </div>

                                    <div className="space-y-1 text-xs">
                                      <p className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 font-medium text-blue-800">
                                        <FileText className="h-3.5 w-3.5" />
                                        {getTotalCasesFromPreview(tcPreview)} cases
                                      </p>
                                      <p className="text-slate-500">
                                        {displayTestCaseVersions.length > 0
                                          ? `${displayTestCaseVersions.length} ${historyLabels.mappingVersionPrefix} version${displayTestCaseVersions.length === 1 ? '' : 's'} with test cases`
                                          : 'No test cases yet'}
                                      </p>
                                    </div>

                                    <div className="space-y-1 text-xs">
                                      <p className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 font-medium text-indigo-800">
                                        <Code2 className="h-3.5 w-3.5" />
                                        {scriptPreview.has_script ? 'Script available' : 'No script yet'}
                                      </p>
                                      <p className="text-slate-500">
                                        {script.count > 0 ? 'Generated script available' : 'No script generated yet'}
                                      </p>
                                    </div>

                                    <div className="space-y-1 text-xs">
                                      <p className="font-semibold text-slate-700">{historyLabels.mappingTitle}</p>
                                      {displayTestCaseVersions.length > 1 ? (
                                        <select
                                          value={activeTestCaseVersion || ''}
                                          disabled={isHistoryRestoring}
                                          onClick={(event) => event.stopPropagation()}
                                          onChange={(event) => {
                                            event.stopPropagation();
                                            const selectedVersion = Number(event.target.value);
                                            if (selectedVersion > 0) {
                                              openSection(selectedVersion);
                                            }
                                          }}
                                          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs font-medium text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                          {displayTestCaseVersions.map((version) => (
                                            <option key={version.version} value={version.version}>
                                              {Number(version.brd_version || 0) > 0 ? `${historyLabels.mappingVersionPrefix} v${version.brd_version} test cases` : `Saved test cases`}
                                              {version.active ? ' - active' : ''}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <p className="text-slate-500">
                                          {displayTestCaseVersions[0]?.brd_version
                                            ? `${historyLabels.mappingVersionPrefix} v${displayTestCaseVersions[0].brd_version} test cases`
                                            : activeTestCaseVersion ? 'Saved test cases' : 'No saved version'}
                                        </p>
                                      )}
                                    </div>

                                    <div>
                                      <button
                                        type="button"
                                        disabled={isHistoryRestoring}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openSection(activeTestCaseVersion);
                                        }}
                                        className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5" />
                                        {isHistoryRestoring ? 'Opening...' : 'View Test Cases'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="p-6 text-sm text-slate-600">
                            <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                              <Sparkles className="h-4 w-4 text-blue-600" />
                              {historyLabels.noGeneratedSections}
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex min-h-[280px] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center">
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    {historyLabels.selectPrompt}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {historyLabels.selectCopy}
                  </p>
                </div>
              </div>
            )}
      </HistoryDashboardShell>

      {deleteDialogOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/35 p-4">
          <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-rose-600">
                {historyLabels.deleteHistoryTitle}
              </p>
              <h3 className="mt-1 text-lg font-bold text-slate-900">
                {historyLabels.deleteTitle(deleteTargetPdf?.pdf_file)}
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                {historyLabels.deleteCopy}
              </p>
            </div>

            <div className="space-y-3 px-5 py-4">
              <p className="text-sm text-slate-700">
                Type <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-900">delete this</span> to confirm.
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(event) => setDeleteConfirmText(event.target.value)}
                placeholder="delete this"
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 outline-none ring-0 transition focus:border-rose-400"
                disabled={deleteSaving}
              />
              {deleteError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {deleteError}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={closeDeleteDialog}
                className="secondary-btn px-4 py-2 text-sm"
                disabled={deleteSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeletePdf}
                className="inline-flex items-center gap-2 rounded-xl border border-rose-700 bg-rose-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-800 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={deleteSaving}
              >
                {deleteSaving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {sharingPanelOpen && canShowSharingButton && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
          <div className="flex max-h-[88dvh] w-full max-w-2xl flex-col overflow-hidden rounded-[26px] border border-white/70 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.34)]">
            <div className="relative border-b border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-blue-50 px-6 pb-5 pt-6">
              <button
                type="button"
                onClick={closeSharingDialog}
                className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/85 text-slate-500 transition-colors hover:bg-white hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={sharingLoading}
                aria-label="Close BRD sharing"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="flex items-start gap-4 pr-10">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 ring-8 ring-emerald-100/45">
                  <Share2 className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
                    BRD Sharing
                  </p>
                  <h3 className="mt-2 truncate text-xl font-bold leading-7 text-slate-950">
                    Share {dashboardPdf?.pdf_file || 'this BRD'}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Manage who can open this BRD and its saved generated outputs.
                  </p>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
              {canShareWithUser && (
                <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
                    <div className="min-w-0 flex-1">
                      <label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                        Add application user
                      </label>
                      <select
                        value={selectedShareUserId}
                        onChange={(event) => setSelectedShareUserId(event.target.value)}
                        className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-emerald-400"
                        disabled={sharingLoading}
                      >
                        <option value="">
                          {eligibleShareUsers.length > 0 ? 'Select application user' : 'No users available'}
                        </option>
                        {eligibleShareUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name || user.email} ({user.email || 'no email'})
                          </option>
                        ))}
                      </select>
                      <p className="mt-2 text-xs text-slate-500">
                        {canDirectShare
                          ? 'Selected users get access immediately.'
                          : 'This creates a sharing request for a Lead/Admin to review.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={handleShareRequest}
                      disabled={shareActionDisabled}
                    >
                      {sharingLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <UserPlus className="h-4 w-4" />
                      )}
                      {shareActionLabel}
                    </button>
                  </div>
                </section>
              )}

              {sharingActionError && (
                <div className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  {sharingActionError}
                </div>
              )}

              {sharingLoading && !normalizedSharingStatus && (
                <div className="flex items-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading sharing details...
                </div>
              )}

              <section className="rounded-2xl border border-slate-200 bg-white">
                <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      People with access
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-slate-900">
                      {sharedUsers.length} shared user{sharedUsers.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <Users className="h-4 w-4 text-slate-400" />
                </div>

                {sharedUsers.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {sharedUsers.map((user) => (
                      <div key={user.id} className="flex items-center justify-between gap-3 px-4 py-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-50 text-sm font-bold text-emerald-700">
                            {String(user.name || user.email || user.id || '?').slice(0, 1).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">
                              {user.name || user.email || user.id}
                            </p>
                            {user.email && (
                              <p className="truncate text-xs text-slate-500">{user.email}</p>
                            )}
                          </div>
                        </div>
                        {canDirectShare && (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                            onClick={() => openRevokeDialog(user)}
                            disabled={sharingLoading}
                          >
                            <UserMinus className="h-3.5 w-3.5" />
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-5 text-sm text-slate-500">
                    No direct shared users yet.
                  </div>
                )}
              </section>

              {(canApproveSharing || pendingShareRequests.length > 0) && (
                <section className="rounded-2xl border border-slate-200 bg-white">
                  <div className="border-b border-slate-100 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Pending share requests
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-slate-900">
                      {pendingShareRequests.length} pending
                    </p>
                  </div>

                  {pendingShareRequests.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {pendingShareRequests.map((requestRow) => (
                        <div
                          key={requestRow.id}
                          className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">
                              {requestRow.target_user_name || requestRow.target_user_email || requestRow.target_user_id}
                            </p>
                            <p className="truncate text-xs text-slate-500">
                              Requested by {requestRow.requested_by_name || requestRow.requested_by_email || requestRow.requested_by_user_id}
                            </p>
                          </div>
                          {canApproveSharing && (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="primary-btn px-3 py-1.5 text-xs"
                                onClick={() => handleApproveShareRequest(requestRow.id)}
                                disabled={sharingLoading}
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                className="secondary-btn px-3 py-1.5 text-xs"
                                onClick={() => handleRejectShareRequest(requestRow.id)}
                                disabled={sharingLoading}
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-5 text-sm text-slate-500">
                      No pending share requests.
                    </div>
                  )}
                </section>
              )}
            </div>

            <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-6 py-4">
              <button
                type="button"
                onClick={closeSharingDialog}
                className="secondary-btn px-4 py-2 text-sm"
                disabled={sharingLoading}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {revokeTargetUser && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-[460px] overflow-hidden rounded-[24px] border border-white/70 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.32)]">
            <div className="relative border-b border-slate-200 bg-gradient-to-br from-rose-50 via-white to-slate-50 px-6 pb-5 pt-6">
              <button
                type="button"
                onClick={closeRevokeDialog}
                className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-slate-500 transition-colors hover:bg-white hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={revokeSaving}
                aria-label="Close remove access dialog"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="flex items-start gap-4 pr-10">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-rose-100 text-rose-700 ring-8 ring-rose-100/45">
                  <UserMinus className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-rose-600">
                    Remove Access
                  </p>
                  <h3 className="mt-2 text-xl font-bold leading-7 text-slate-950">
                    Remove BRD access?
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    This user will lose access to the shared BRD and saved outputs tied to it.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white text-sm font-bold text-slate-700 shadow-sm">
                  {String(revokeTargetUser.name || revokeTargetUser.email || revokeTargetUser.id || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                    User
                  </p>
                  <p className="mt-0.5 truncate text-sm font-semibold text-slate-950">
                    {revokeTargetUser.name || revokeTargetUser.email || revokeTargetUser.id}
                  </p>
                  {revokeTargetUser.email && (
                    <p className="mt-0.5 truncate text-xs text-slate-500">{revokeTargetUser.email}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-5 text-amber-800">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                Removing access will not delete the BRD history. It only removes this user's permission to open it.
              </div>

              {revokeError && (
                <div className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  {revokeError}
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeRevokeDialog}
                className="secondary-btn justify-center px-4 py-2 text-sm"
                disabled={revokeSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRevokeShareAccess}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-700 bg-rose-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-rose-800 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={revokeSaving}
              >
                {revokeSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  <>
                    <UserMinus className="h-4 w-4" />
                    Remove Access
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};
